import React, { useContext, useEffect, useCallback, useState } from 'react';

import makeStyles from '@material-ui/core/styles/makeStyles';

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

import TextInput from '../../../Components/Inputs/TextInput';
import TextAreaInput from '../../../Components/Inputs/TextAreaInput';

import {
  SetCurrentStepIndexContext,
  StepperDataContext,
  StepperDataDispatchContext,
  CurrentStepIndexContext,
  StepperSubmitContext
} from '../../../Contexts/StepperContext';

import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import clsx from 'clsx';
import { CUSTOM_ATTACHMENTS, DEFAULT_SUBJECTS } from '../Pqr_enums';
import { convertBytesToMegaBytes } from '../../../Utils/Calculations/file';
import AttachmentSection from './AttachmentSection/AttachmentSection';
import { maxMBSizePqrFiles } from '../../../Configs/general';

const schema = yup.object({
  subject: yup
    .string()
    .max(250, 'Máximo 250 carácteres')
    .test(
      'no-double-quotes',
      'El asunto no debe contener comillas dobles (")',
      value => !value.match(/"/)
    )
    .required('Ingrese el asunto'),
  description: yup
    .string()
    .max(2000, 'Máximo 2000 carácteres')
    .required('Ingrese la descripción')
});

const DetailStep = props => {
  const {
    formId,
    setGoBack,
    setCurrentStep: setVisualStepperIndex,
    requestType,
    setNextButtonText
  } = props;

  const classes = useStyles();

  const currentStepIndex = useContext(CurrentStepIndexContext);
  const setCurrentStep = useContext(SetCurrentStepIndexContext);
  const setData = useContext(StepperDataDispatchContext);
  const stepperData = useContext(StepperDataContext);
  const submit = useContext(StepperSubmitContext);

  const fileInputId = 'Pqr_detail_fileinput';

  const { register, getValues, errors, handleSubmit } = useForm({
    validationSchema: schema,
    defaultValues: {
      ...stepperData,
      subject: DEFAULT_SUBJECTS[requestType] || ''
    }
  });

  const initialFiles = stepperData.attachments || [];

  const [files, setFiles] = useState(initialFiles);
  const [totalSize, setTotalSize] = useState(
    convertBytesToMegaBytes(
      initialFiles.reduce((total, file) => total + file.size, 0)
    )
  );

  const onForward = useCallback(
    values => {
      const attachmentSetupList = CUSTOM_ATTACHMENTS[requestType] || [];
      const missingFiles = attachmentSetupList.filter(
        (settings, index) => settings.required && !files[index]
      );

      const fileErrors = files.filter(file => file.error);

      if (
        fileErrors.length === 0 &&
        totalSize <= maxMBSizePqrFiles &&
        missingFiles.length === 0
      ) {
        setData(data => ({ ...data, ...values, attachments: files }));
        if (submit) {
          submit({ ...stepperData, ...values, attachments: files });
        }
        return;
      }

      if (!requestType) {
        const fileInputButton = document.getElementById(fileInputId);
        fileInputButton.scrollIntoView();
        return;
      }

      if (attachmentSetupList.length > 0) {
        //set any required file as null so the input can display an error.
        const parsedFiles = attachmentSetupList.map((setup, index) => {
          if (setup.required && !files[index]) {
            return null;
          }

          return files[index];
        });

        setFiles(parsedFiles);
      }
    },
    [files, requestType, stepperData, setData, submit, totalSize, fileInputId]
  );

  const onBackward = useCallback(() => {
    setData(data => ({
      ...data,
      ...getValues(),
      attachments: files
    }));
    setCurrentStep(currentStep => currentStep - 1);
  }, [getValues, setCurrentStep, setData, files]);

  useEffect(() => {
    setNextButtonText('Enviar');
    setVisualStepperIndex(currentStepIndex);
    setGoBack({
      action: onBackward
    });
  }, [
    setGoBack,
    onBackward,
    setVisualStepperIndex,
    currentStepIndex,
    setNextButtonText,
    register
  ]);

  return (
    <div
      className={clsx(classes.container, {
        [classes.filelistActive]: files && files.length > 0
      })}
    >
      <Typography className={classes.title}>Detalle de la solicitud</Typography>
      <form id={formId} onSubmit={handleSubmit(onForward)}>
        <Grid container className={classes.inputContainer}>
          <Grid item xs={12}>
            <TextInput
              label="Asunto"
              inputRef={register}
              inputProps={{ maxLength: 250 }}
              fullWidth
              name="subject"
              helperText={errors.subject && errors.subject.message}
              error={Boolean(errors.subject)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextAreaInput
              id="Pqr_detail_textareainput"
              ref={register}
              margin="normal"
              label="Detalle su solicitud"
              name="description"
              rowsMax={10}
              maxLength={2000}
              helperText={errors.description && errors.description.message}
              error={Boolean(errors.description)}
            />
          </Grid>
          <AttachmentSection
            requestType={requestType}
            classes={classes}
            fileInputId={fileInputId}
            files={files}
            setFiles={setFiles}
            totalSize={totalSize}
            setTotalSize={setTotalSize}
          />
        </Grid>
      </form>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(4)
  },
  title: {
    fontSize: 18,
    fontWeight: 700
  },
  subtitle: {
    fontSize: 14
  },
  inputContainer: {
    marginTop: theme.spacing()
  },
  inputContainerItem: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing()
  },
  filelistItem: {
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing()
    }
  },
  filelistActive: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: 58
    }
  },
  fileButtonText: {
    color: '#626262'
  },
  sweetAlertContainer: {
    flexGrow: 1,
    [theme.breakpoints.up('sm')]: {
      paddingLeft: theme.spacing(2)
    }
  },
  sweetAlert: {
    [theme.breakpoints.up('sm')]: {
      height: 58
    }
  },
  sweetAlertText: {
    fontSize: 14
  },
  noWrap: {
    whiteSpace: 'nowrap'
  }
}));

export default DetailStep;
